import type { Move } from '../../../schemas/move';

/**
 * Mosse avanzate del Bardo L6-10 (manuale V1, rr. 2506-2601). 14 mosse.
 *
 * Replaces presenti:
 *  - *Io Sono Leggenda* SOSTITUISCE *Conoscitore di Leggende*
 *  - *Truffa* SOSTITUISCE *Raggirare*
 *
 * Prerequisiti notabili:
 *  - 3 mosse richiedono *Parole di Potere* (Bisbigli Insinuanti, Accento
 *    Cristallino, Parole Risonanti)
 *  - *Subdolo* richiede *Affascinante e Onesto*
 *  - *Colpo di Scena* richiede *Piano d'Azione*
 *  - *Il Brivido del Rischio* richiede *Scavezzacollo*
 *  - *Cadenza Perfetta* richiede *Tenere il Ritmo*
 */

const bisbigliInsinuanti: Move = {
  id: 'bardo.bisbigli-insinuanti',
  name: 'Bisbigli Insinuanti',
  type: 'advanced_6_10',
  classId: 'bardo',
  trigger: 'Quando parli a una creatura mortale e senziente che può sentirti',
  description:
    "Riesci a far assumere alla tua voce timbri insoliti e suadenti. Scegline due (alla creazione della mossa):\n\n- *timbro ipnotico*: il tuo bersaglio esegue un tuo singolo ordine al meglio delle sue possibilità. L'ordine non deve essere autolesionista;\n- *timbro offuscante*: cancelli dalla memoria del tuo bersaglio ciò che è appena successo;\n- *timbro terrificante*: il tuo bersaglio scappa o si rannicchia in preda al terrore, a discrezione del GM;\n- *timbro illusorio*: mostri un'illusione credibile e realistica al tuo bersaglio;\n- *timbro incapacitante*: il bersaglio non può utilizzare una sua tecnica, capacità o potere a tua scelta.\n\nQuando usi la mossa, scegli quale timbro di voce vuoi usare e tira+**CAR**. Con 7+, ottieni l'effetto previsto. Con un 7-9, scegli una limitazione:\n\n- l'effetto dura poco: se vuoi sfruttarlo devi sbrigarti;\n- il potere ti danneggia: subisci una Debilitazione a scelta del GM;\n- attiri attenzioni indesiderate o ti metti nei guai.",
  roll: {
    stat: 'carisma',
    on10plus: { text: "Ottieni l'effetto del timbro scelto senza limitazioni." },
    on7to9: {
      text: "Ottieni l'effetto, ma scegli una limitazione (durata breve / Debilitazione / guai).",
    },
  },
  tags: [],
  prerequisites: ['bardo.parole-di-potere'],
  replaces: [],
  // Le 5 sotto-scelte (timbri) sono "scegline 2" alla creazione della mossa.
  // Modello come `choices` con `choiceCount: 2`. Riuso del pattern Avvelenatore
  // (Ladro), questa volta con `choiceCount > 1`.
  choices: [
    {
      id: 'timbro-ipnotico',
      label: 'Timbro ipnotico',
      description: 'Il bersaglio esegue un tuo singolo ordine non autolesionista.',
    },
    {
      id: 'timbro-offuscante',
      label: 'Timbro offuscante',
      description: 'Cancelli dalla memoria del bersaglio ciò che è appena successo.',
    },
    {
      id: 'timbro-terrificante',
      label: 'Timbro terrificante',
      description: 'Il bersaglio scappa o si rannicchia in preda al terrore.',
    },
    {
      id: 'timbro-illusorio',
      label: 'Timbro illusorio',
      description: "Mostri un'illusione credibile al bersaglio.",
    },
    {
      id: 'timbro-incapacitante',
      label: 'Timbro incapacitante',
      description: 'Il bersaglio non può usare una tecnica/capacità/potere.',
    },
  ],
  choiceCount: 2,
};

const accentoCristallino: Move = {
  id: 'bardo.accento-cristallino',
  name: 'Accento Cristallino',
  type: 'advanced_6_10',
  classId: 'bardo',
  trigger: 'In ogni momento (estende *Parole di Potere*)',
  description:
    "Aggiungi le seguenti opzioni a *Parole di Potere*:\n\n- viene liberato da effetti di controllo mentale;\n- plachi una sua forte emozione (gioia, terrore, ira, ecc.). L'alleato prende +1 al prossimo tiro se sfrutta questa cosa.\n\nSe le emozioni o il controllo mentale sono particolarmente forti (a discrezione del GM), gli effetti non sono annullati, ma solo sospesi per qualche tempo.",
  tags: [],
  prerequisites: ['bardo.parole-di-potere'],
  replaces: [],
};

const paroleRisonanti: Move = {
  id: 'bardo.parole-risonanti',
  name: 'Parole Risonanti',
  type: 'advanced_6_10',
  classId: 'bardo',
  trigger: 'Quando usi *Parole di Potere*',
  description:
    'Scegli un alleato e due effetti diversi, o scegli due alleati e applica un effetto a ciascuno (lo stesso o diverso).',
  tags: [],
  prerequisites: ['bardo.parole-di-potere'],
  replaces: [],
};

const orecchioPerLaMagia: Move = {
  id: 'bardo.orecchio-per-la-magia',
  name: 'Orecchio per la Magia',
  type: 'advanced_6_10',
  classId: 'bardo',
  trigger: 'Quando senti qualcuno pronunciare una formula magica',
  description:
    'Il GM ti dirà il nome della magia e i suoi effetti. Prendi +1 al prossimo tiro quando agisci in base a ciò che ti è stato detto.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const ioSonoLeggenda: Move = {
  id: 'bardo.io-sono-leggenda',
  name: 'Io Sono Leggenda',
  type: 'advanced_6_10',
  classId: 'bardo',
  trigger:
    'Quando *declami conoscenze* riguardo qualcosa che rientra in una delle tue aree di specializzazione di *Conoscenze Bardiche*',
  description:
    "Ottieni +1 al tiro. Con 10+, inoltre, puoi fare una domanda qualsiasi sull'argomento al GM, che deve rispondere onestamente.",
  tags: [],
  prerequisites: [],
  replaces: ['bardo.conoscitore-di-leggende'],
};

const subdolo: Move = {
  id: 'bardo.subdolo',
  name: 'Subdolo',
  type: 'advanced_6_10',
  classId: 'bardo',
  trigger: 'Quando usi *Affascinante e Onesto*',
  description:
    'Aggiungi alla lista delle domande che puoi fare «*In che modo sei vulnerabile a me?*». Il bersaglio non può porti questa stessa domanda.',
  tags: [],
  prerequisites: ['bardo.affascinante-e-onesto'],
  replaces: [],
};

const truffa: Move = {
  id: 'bardo.truffa',
  name: 'Truffa',
  type: 'advanced_6_10',
  classId: 'bardo',
  trigger: 'Quando *parlamenti* con qualcuno',
  description:
    'Con un 7+, prendi anche +1 al prossimo tiro contro di lui. Puoi, inoltre, fare una domanda al giocatore che lo interpreta (il GM se è un PNG), che dovrà risponderti sinceramente.',
  tags: [],
  prerequisites: [],
  replaces: ['bardo.raggirare'],
};

const reputazione: Move = {
  id: 'bardo.reputazione',
  name: 'Reputazione',
  type: 'advanced_6_10',
  classId: 'bardo',
  trigger:
    'Quando incontri per la prima volta qualcuno che ha sentito canzoni o leggende che parlano di te',
  description:
    "Tira+**CAR**. Con un 10+, dì al GM due cose che ha sentito su di te. Con un 7-9 tu dì al GM una cosa, e lui ti dirà l'altra.",
  roll: {
    stat: 'carisma',
    on10plus: { text: 'Dici al GM due cose che ha sentito su di te.' },
    on7to9: { text: "Dici al GM una cosa, lui ti dice l'altra." },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const pendonoDallaMiaPenna: Move = {
  id: 'bardo.pendono-dalla-mia-penna',
  name: 'Pendono dalla Mia Penna',
  type: 'advanced_6_10',
  classId: 'bardo',
  trigger:
    'Quando hai finalmente finito di comporre un poema o una ballata, oppure di scrivere un libro',
  description:
    'Tira+**CAR**. Il suo contenuto si diffonderà abbastanza rapidamente in giro. Con un 10+, gran parte della gente ci crede. Con un 7-9, quel che hai raccontato viene percepito come diceria o leggenda, ma con un fondo di verità. Con 6-, il contenuto ti si rivolge contro: il GM saprà come fare!',
  roll: {
    stat: 'carisma',
    on10plus: { text: 'Gran parte della gente ci crede.' },
    on7to9: { text: 'Percepito come diceria o leggenda, ma con un fondo di verità.' },
    on6minus: { text: 'Il contenuto ti si rivolge contro, il GM saprà come fare.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const colpoDiScena: Move = {
  id: 'bardo.colpo-di-scena',
  name: 'Colpo di Scena',
  type: 'advanced_6_10',
  classId: 'bardo',
  trigger:
    'Quando giri i tacchi e te ne vai, proprio un istante prima che il nemico possa scagliare un attacco contro di te',
  description:
    'Tira+**CAR**. Con 10+, ti allontani, e puoi tornare quando gli altri meno se lo aspettano. Con 7-9, ti allontani comunque, ma non potrai tornare finché il combattimento non è finito.',
  roll: {
    stat: 'carisma',
    on10plus: { text: 'Ti allontani; puoi tornare a sorpresa quando vuoi.' },
    on7to9: { text: 'Ti allontani; non potrai tornare finché il combattimento non è finito.' },
  },
  tags: [],
  prerequisites: ['bardo.piano-d-azione'],
  replaces: [],
};

const ilBrividoDelRischio: Move = {
  id: 'bardo.il-brivido-del-rischio',
  name: 'Il Brivido del Rischio',
  type: 'advanced_6_10',
  classId: 'bardo',
  trigger: 'Quando un nemico ti ha in pugno, o metti in atto un piano eccessivamente sconsiderato',
  description:
    'Hai +1 a *sfidare il pericolo*. Con 12+, inoltre, ribalti la situazione a tuo vantaggio: il GM ti dirà come.',
  tags: [],
  prerequisites: ['bardo.scavezzacollo'],
  replaces: [],
};

const colpireDArguzia: Move = {
  id: 'bardo.colpire-d-arguzia',
  name: "Colpire d'Arguzia",
  type: 'advanced_6_10',
  classId: 'bardo',
  trigger: "Quando *tagli e spacchi* con un'arma *precisa*",
  description:
    "Il tuo attacco ottiene l'etichetta +1 *perforante*. Con 12+, inoltre, puoi sbeffeggiare l'avversario dall'alto della tua superiorità e scegliere un'opzione tra:\n\n- s'infuria: hai tutta la sua attenzione, almeno per qualche cruciale momento;\n- riconosce la tua maestria: ti lascia andare senza provare a colpirti;\n- si demoralizza: prendi +1 al prossimo tiro di *taglia e spacca* contro di lui.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const cadenzaPerfetta: Move = {
  id: 'bardo.cadenza-perfetta',
  name: 'Cadenza Perfetta',
  type: 'advanced_6_10',
  classId: 'bardo',
  trigger: 'Quando eviti un attacco avversario grazie a *Tenere il Ritmo*',
  description:
    "Sarai in grado di evitare tutti i suoi attacchi successivi, senza dover tirare i dadi. L'effetto termina non appena fai qualsiasi altra cosa che non sia *Tenere il Ritmo* di quell'avversario.",
  tags: [],
  prerequisites: ['bardo.tenere-il-ritmo'],
  replaces: [],
};

const secondoLavoro: Move = {
  id: 'bardo.secondo-lavoro',
  name: 'Secondo Lavoro',
  type: 'advanced_6_10',
  classId: 'bardo',
  trigger: 'Al level-up (livello 6-10), come scelta multiclasse',
  description:
    "Mossa *multiclasse*. Scegli una mossa qualsiasi da un'altra classe. Non scegliere una mossa multiclasse.",
  tags: ['multiclasse'],
  prerequisites: [],
  replaces: [],
  // FI-17 (D7): pool aperto senza cap livello.
  multiclass: {
    picks: { kind: 'any-other-class' },
  },
};

export const bardoAdvanced6to10Moves: readonly Move[] = [
  bisbigliInsinuanti,
  accentoCristallino,
  paroleRisonanti,
  orecchioPerLaMagia,
  ioSonoLeggenda,
  subdolo,
  truffa,
  reputazione,
  pendonoDallaMiaPenna,
  colpoDiScena,
  ilBrividoDelRischio,
  colpireDArguzia,
  cadenzaPerfetta,
  secondoLavoro,
] as const;
